import { Assembler, isValidBusinessObject, NodeEdgeMapper, ThingShadowState, User } from '@iotv/datamodel';
import { NavSections } from '../../components/navigation/NavSections';
import config from '../../config';
import { AppLocalStorage } from '../../data/LocalStorage/AppLocalStorage';
import { Severity } from '../../types/AppTypes';
import { ACTIONS } from '../actions/actionTypes';
import { addErrorToUpdate, addMessageToUpdate, getDefaultRouteFromNormalData, getUserFromNormalData } from './helpers';
const debug = process.env.REACT_APP_DEBUG && false;
const redirectDebug = process.env.REACT_APP_DEBUG && false;
const getInitialState = () => {
  return {
    cognitoUser: undefined,
    userGroupRoles: {
      groups: [],
      roles: [],
      preferredRole: undefined
    },
    userCustomers: new NodeEdgeMapper(),
    contextProperty: undefined,
    contextCustomer: undefined,
    contextSecondaryObject: undefined,
    roleArbitrationInstance: undefined,
    userSelectedGroup: AppLocalStorage.get(NavSections.HOME, 'lastUserSelectedGroupName') ?? undefined,
    user: undefined,
    applicationData: new NodeEdgeMapper(),
    largeObjects: {},
    messages: {},
    errors: {},
    redirect: undefined,
    userGeolocation: undefined,
    pinger: false,
    useSpecializedViews: config.app.webUI?.useSpecializedViews ?? false,
    defaultRoute: AppLocalStorage.get(NavSections.HOME, 'lastViewedRoute') ?? config.app.initialRedirect,
    thingViewMap: undefined,
    controlsMap: undefined,
    domainTreeMapping: undefined,
    externalTreeDefinitionMap: undefined,
    externalMenuOrdering: null
  };
};
/** where reducer mutations must replace the applicationData nodeEdgeMapper
 * as of 2024 Jan experimental, works for objectTree, uncertain side effects ( eg focus ) on other componnets
 */
const cloneApplicationData = ({
  state,
  update
}) => {
  const clonedApplicationData = new NodeEdgeMapper(state.applicationData);
  update.applicationData = clonedApplicationData;
  return update;
};
//const removeMessageFromUpdate = ( messa)
const AppReducer = (state = getInitialState(), action) => {
  debug && action.type !== ACTIONS.CLEAR_MESSAGE_PING && console.log('App Reducer action', action);
  // pre loaded project configs need to persist post login
  const {
    thingViewMap,
    externalTreeDefinitionMap,
    domainTreeMapping,
    controlsMap
  } = state;
  const preLoads = {
    thingViewMap,
    externalTreeDefinitionMap,
    domainTreeMapping,
    controlsMap
  };
  const update = {
    ...state
  };
  const redirect = update.redirect = undefined;
  if (redirect) console.log('set redirect undefined', redirect);
  redirectDebug && console.log('State has redirect', state.redirect);
  switch (action.type) {
    case ACTIONS.RECEIVE_THING_TYPES_MAP:
      {
        const castVal = action ?? {};
        if (castVal.value) {
          Object.assign(update, {
            thingViewMap: castVal.value
          });
        }
        return update;
      }
    case ACTIONS.SET_USER_MESSAGE:
      {
        debug && console.log('ACTION.SET_USER_MESSAGE:', action);
        let messagesUpdate = {
          messages: {
            ...state.messages
          }
        };
        messagesUpdate.messages[action.value.id] = action.value;
        return Object.assign({}, state, messagesUpdate);
      }
    case ACTIONS.DISMISS_USER_MESSAGE:
      {
        let messagesUpdate = {
          messages: {
            ...state.messages
          }
        };
        const messageId = action.value.id;
        if (messagesUpdate.messages[messageId]) {
          debug && console.log(`ACTIONS.DISMISS_USER_MESSAGE deleting ${messageId} from `, messagesUpdate.messages);
          delete messagesUpdate.messages[messageId];
        }
        return Object.assign({}, state, messagesUpdate);
      }
    case ACTIONS.RECEIVE_AUTHENTICATE:
      {
        debug && console.log('RECEIVE_AUTHENTICATE updating state with', update);
        const {
          cognitoUser,
          currentGroup,
          roleArbitrationInstance,
          userGroupRoles
        } = action;
        Object.assign(update, {
          cognitoUser,
          roleArbitrationInstance,
          userGroupRoles,
          ...preLoads
        });
        update.userSelectedGroup = currentGroup;
        const lastUser = AppLocalStorage.get(NavSections.HOME, 'lastContextUser');
        // @ts-expect-error `attributes` does not exist on type `CognitoUser`
        const userMatches = cognitoUser.attributes.sub === lastUser?.id;
        if (lastUser !== undefined && userMatches) update.user = new User(lastUser);
        const lastCustomer = AppLocalStorage.get(NavSections.HOME, 'lastContextCustomer');
        if (userMatches) update.contextCustomer = lastCustomer;
        const lastSecondaryObject = AppLocalStorage.get(NavSections.HOME, 'lastContextSecondaryObject');
        if (userMatches) update.contextSecondaryObject = lastSecondaryObject;
        const lastProperty = AppLocalStorage.get(NavSections.HOME, 'lastContextProperty');
        if (userMatches) update.contextProperty = lastProperty;
        if (userMatches) {
          update.user = lastUser;
          update.contextCustomer = lastCustomer;
          update.contextSecondaryObject = lastSecondaryObject;
          update.contextProperty = lastProperty;
        } else {
          AppLocalStorage.set(NavSections.HOME, 'lastContextCustomer', undefined);
          AppLocalStorage.set(NavSections.HOME, 'lastContextSecondaryObject', undefined);
          AppLocalStorage.set(NavSections.HOME, 'lastContextProperty', undefined);
        }
        return update;
      }
    case ACTIONS.REQUEST_REFRESH_WITH_USER_TOKEN:
      {
        debug && console.log('refresh', action);
        return {
          ...state
        };
      }
    case ACTIONS.RECIEIVE_REFRESH_WITH_USER_TOKEN:
      {
        debug && console.log('refresh', action);
        return {
          ...state
        };
      }
    case ACTIONS.REQUEST_INIT:
      return Object.assign({}, update, {
        didInvalidate: false
      });
    case ACTIONS.RECEIVE_INIT:
      {
        debug && console.log('in receive init - no clone needed as new application data object');
        const applicationData = new NodeEdgeMapper(action.nodeEdgeMapper);
        const user = getUserFromNormalData(state?.cognitoUser, applicationData);
        if (user !== undefined) AppLocalStorage.set(NavSections.HOME, 'lastContextUser', new User(user));
        // debug && console.log('merging', { source: applicationData.containedTypes(), target: update.applicationData.containedTypes()})
        // debug && console.log('setting user to', user)
        update.applicationData.union(applicationData);
        const mergedUpdate = Object.assign({}, update, {
          didInvalidate: false,
          user: user !== undefined ? new User(user) : undefined,
          defaultRoute: getDefaultRouteFromNormalData(applicationData)
        });
        debug && console.log('returning merged state', mergedUpdate);
        return mergedUpdate;
      }
    case ACTIONS.RECEIVE_CONTEXT_CUSTOMERS:
      debug && console.log(' ACTIONS.RECEIVE_CONTEXT_CUSTOMERS ', action);
      update.userCustomers = action.customers;
      const customers = update.userCustomers.getNodesOfType('Customer');
      if (customers.length === 1) {
        update.contextCustomer = customers[0];
      } else {
        const lastCustomer = customers.find(item => item.sk === AppLocalStorage.get(NavSections.HOME, 'lastContextCustomerSk'), undefined);
        update.contextCustomer = lastCustomer ?? customers[0];
      }
      AppLocalStorage.set(NavSections.HOME, 'lastContextCustomer', update.contextCustomer);
      AppLocalStorage.set(NavSections.HOME, 'lastContextCustomerSk', update.contextCustomer?.sk);
      debug && console.log(' ACTIONS.RECEIVE_CONTEXT_CUSTOMERS UPDATE ', update);
      return update;
    case ACTIONS.MQTT_MESSAGE_RECEIVED:
      {
        /** 2024 JAN not adding cloneApplicationData here yet */
        let incomingOb;
        if (action.key === 'shadowAccepted') {
          const now = new Date();
          debug && console.log(`adding TSS to state at ${now.getMinutes()} ${now.getSeconds()} ${now.getMilliseconds()} from`, action.value);
          incomingOb = new ThingShadowState({
            type: 'ThingShadowState',
            payload: {
              ...action.value
            },
            id: new Date().valueOf().toString(),
            pk: action.value.thingSk
          });
        } else if (action.key === 'alertIssued') {
          incomingOb = Assembler.getInstance(action.value);
        }
        update.pinger = true;
        if (incomingOb && update.applicationData && isValidBusinessObject(incomingOb)) {
          update.applicationData.loadItem(incomingOb);
          debug && console.log(`Added ${incomingOb.type} to state`);
        } else {
          debug && console.log('get non VOB', incomingOb);
        }
        return update;
      }
    case ACTIONS.MQTT_NETWORK_MESSAGE_RECEIVED:
      {
        /** 2024 JAN not adding cloneApplicationData here yet */
        const objects = action.value;
        update.pinger = true;
        if (objects instanceof Array) {
          objects.forEach(object => {
            if (update.applicationData && isValidBusinessObject(object)) {
              update.applicationData.loadItem(object);
              debug && console.log('Added to normal', object);
            } else {
              debug && console.log('Not valid', object);
            }
          });
        }
        return update;
      }
    case ACTIONS.CLEAR_MESSAGE_PING:
      {
        update.pinger = false;
        return update;
      }
    case ACTIONS.RECEIVE_SAVE_TRANSACTION_RESULT:
      {
        if (action.value?.err) {
          update.messages = {
            ...state.messages
          };
          addErrorToUpdate(action.value.err, update);
        } else if (action.value?.data) {
          debug && console.log('Result', action.value);
          debug && console.log(`AppReducer ${action.type} repalcing application data node edge mapper with ${Object.keys(update.applicationData.pkMapped)} keys, and loading saved item`);
          cloneApplicationData({
            state,
            update
          });
          const newObject = action.value.data;
          update.applicationData.loadItem(newObject);
          if (newObject.name) {
            const label = 'Save result',
              content = `${newObject?.name} was saved successfully`,
              type = 'SnackBar';
            addMessageToUpdate(update, label, content, type);
          }
        } else {
          debug && console.log('System returned no save result, does the api datamodel contain this type?');
        }
        return update;
      }
    case ACTIONS.RECEIVE_GET_ADJACENT_RESULT:
      {
        const errData = action.value;
        if (errData.err) {
          console.log('Error in GetAdjacent', errData.err);
        } else if (errData.data) {
          const typeCount = {};
          errData.data.forEach(item => {
            if (!typeCount[item.type]) {
              typeCount[item.type] = 1;
            } else {
              typeCount[item.type]++;
            }
            debug && console.log(`AppReducer ${action.type} repalcing application data node edge mapper and loading adjacent items`);
            cloneApplicationData({
              state,
              update
            });
            update.applicationData.loadItem(item);
          });
          debug && console.log('Added these types to state', typeCount);
        }
        debug && console.log('Updated Normal Data in RECEIVE_GET_ADJACENT_RESULT', update);
        return update;
      }
    case ACTIONS.REQUEST_ADD_OBJECTS_TO_STATE:
      {
        const vobs = action.value;
        if (vobs instanceof Array) {
          debug && console.log(`AppReducer ${action.type} repalcing application data node edge mapper and loading items`);
          cloneApplicationData({
            state,
            update
          });
          update.applicationData.loadItems(vobs.filter(x => isValidBusinessObject(x)));
        } else if (vobs instanceof NodeEdgeMapper) {}
        return update;
      }
    case ACTIONS.REQUEST_RENOVE_OBJECTS_FROM_STATE:
      {
        const vobs = action.value;
        if (vobs instanceof Array) {
          /*vobs.filter( ( x ) => isValidBusinessObject( x )).forEach( ( vob ) => update.applicationData.removeItem( vob ))*/
          debug && console.log(`AppReducer ${action.type}  replacing application data nodeedgemapper and removing items`, vobs);
          cloneApplicationData({
            state,
            update
          });
          vobs.filter(x => isValidBusinessObject(x)).forEach(vob => update.applicationData.removeItem(vob));
          if (debug) {
            const nonRemovedItems = vobs.filter(item => update.applicationData.getItem(item));
            if (nonRemovedItems.length > 0) {
              console.log(`WARN: REMOVE_OBJECTS_FROM_STATE did not remove`, nonRemovedItems);
            }
          }
        } else {}
        return update;
      }
    case ACTIONS.RECEIVE_GET_LARGE_OBJECT:
      {
        const errData = action.value;
        if (errData.err) {
          console.log('Error in GetAdjacent', errData.err);
        } else if (errData.data) {
          debug && console.log('Received Large Object', errData.data);
          if (errData.data) {
            const largeObject = errData.data;
            const largeObjectKey = largeObject.sk;
            update.largeObjects[largeObjectKey] = largeObject;
          }
        }
        return update;
      }
    case ACTIONS.REQUEST_NEW_LIST_OBJECT:
      {
        if (action.newObject && action.newEdgeObject) {
          const label = '',
            content = `You are drafting a new ${action.newObject.type}`,
            type = 'SnackBar';
          update.applicationData.loadItems([action.newEdgeObject, action.newObject]);
          addMessageToUpdate(update, label, content, type);
          return update;
        }
        return {
          ...state
        };
      }
    case ACTIONS.RECEIVE_SAVE_AND_LINK_TRANSACTION_RESULT:
      {
        if (action.value) {
          const saveAndLinkResponse = action.value;
          debug && console.log(`AppReducer ${action.type} repalcing application data node edge mapper and loading save and link items`);
          cloneApplicationData({
            state,
            update
          });
          const {
            edge,
            source,
            target
          } = saveAndLinkResponse;
          if (edge && source && target) {
            const label = 'Link result',
              content = `${target.name} saved and linked to ${source.name}`,
              type = 'SnackBar';
            update.applicationData.loadItems([source, target, edge]);
            /** it would unclear which was the linker and which was the linkee but addMessageToUpdate doesnt use  */
            addMessageToUpdate(update, label, content, type);
          } else {
            console.log('WARN Bad S&L result no valid response items, action was', {
              action,
              saveAndLinkResponse
            });
          }
        } else {
          console.log('WARN Bad S&L result, action was', action);
          // throw new Error(
          //   'Problem in Link'
          // )
        }
        /** IN DEPRECATION */
        if (action.value?.err) {
          update.messages = {
            ...state.messages
          };
          addErrorToUpdate(action.value.err, update);
          throw new Error('deprecated RECEIVE_SAVE_AND_LINK_TRANSACTION_RESULT return');
        } else if (action.value?.data instanceof Array && isValidBusinessObject(action.value.data[1])) {
          debug && console.log(`AppReducer (DEPRECATED SAVE AND LINK PART) repalcing application data node edge mapper and loading save and link items`);
          cloneApplicationData({
            state,
            update
          });
          const newObject = action.value.data[1];
          const label = 'Link result',
            content = `${action.value.data[1].name} was saved successfully`,
            type = 'SnackBar';
          update.applicationData.loadItem(newObject);
          addMessageToUpdate(update, label, content, type);
          throw new Error('deprecated RECEIVE_SAVE_AND_LINK_TRANSACTION_RESULT return');
        }
        return update; //Object.assign( {}, state, update );
      }
    case ACTIONS.RECEIVE_LINK_OBJECTS_TO_PRIMARY:
      {
        if (action.value) {
          const linkObjectsResponse = action.value;
          debug && console.log(`AppReducer ${action.type} repalcing application data node edge mapper and loading save and link items`);
          cloneApplicationData({
            state,
            update
          });
          const loadableItems = linkObjectsResponse.map(({
            edge,
            source,
            target
          }) => [edge, source, target]).flat();
          update.applicationData.loadItems(loadableItems);
          const label = 'Link result';
          const content = `${linkObjectsResponse.length} items were linked successfully`;
          const type = 'SnackBar';
          /** it would unclear which was the linker and which was the linkee but addMessageToUpdate doesnt use  */
          addMessageToUpdate(update, label, content, type);
        } else {
          console.log('WARN Bad S&L result, action was', action);
          // throw new Error(
          //   'Problem in Link'
          // )
        }
        return update;
      }
    case ACTIONS.RECEIVE_DELETE_TRANSACTION_RESULT:
      {
        const response = action.value;
        if (response.err) {
          update.messages = {
            ...state.messages
          };
          const label = 'Delate Error',
            content = `Remove ERR: ${response.err.message}`,
            type = 'MessageBar',
            severity = Severity.error;
          addMessageToUpdate(update, label, content, type, severity);
        } else if (response.data && isValidBusinessObject(response.data.object)) {
          debug && console.log(`AppReducer ${action.type} repalcing application data node edge mapper and removing items`);
          cloneApplicationData({
            state,
            update
          });
          const oldObject = response.data.object;
          const label = 'Delate result',
            content = `${oldObject.name} was deleted successfully`,
            type = 'SnackBar';
          update.applicationData.removeItem(oldObject);
          addMessageToUpdate(update, label, content, type);
          const lastUrl = response.data.location?.pathname;
          if (lastUrl?.includes(oldObject.id)) update.redirect = `/${NavSections.THINGSVIEW}/${oldObject.type}`;
        } else {
          debug && console.log('action was', action);
          throw new Error('Problem in RECEIVE_DELETE_TRANSACTION_RESULT');
        }
        return update; //Object.assign( {}, state, update );
      }
    case ACTIONS.RECEIVE_UNLINK_OBJECTS_RESULT:
      {
        const response = action.value;
        if (response.err) {
          update.messages = {
            ...state.messages
          };
          addErrorToUpdate(response.err, update);
        } else if (response.data && isValidBusinessObject(response.data.referenceItem) && isValidBusinessObject(response.data.removedItem)) {
          debug && console.log(`AppReducer ${action.type} repalcing application data node edge mapper and unlinking items`);
          cloneApplicationData({
            state,
            update
          });
          const {
            referenceItem,
            removedItem
          } = response.data;
          const label = 'Delate result',
            content = `${removedItem?.name} was unlinked successfully from ${referenceItem?.name}`,
            type = 'SnackBar';
          update.applicationData.removeItem(removedItem);
          addMessageToUpdate(update, label, content, type);
        } else {
          debug && console.log('action was', action);
          throw new Error('Problem in UNLINK_OBJECTS_RESULT');
        }
        return update; //Object.assign( {}, state, update );
      }
    case ACTIONS.CANCEL_REDIRECT_AFTER_SUCCESS:
      {
        debug && console.log('returning update with no redirect', update.redirect);
        return update;
      }
    case ACTIONS.GET_USER_GEOLOCATION:
      {
        return update;
      }
    case ACTIONS.RECEIVE_USER_GEOLOCATION:
      {
        update.userGeolocation = action.position ?? undefined;
        return update;
      }
    case ACTIONS.CHANGE_APP_STATE:
      {
        let update = action.value;
        //TODO General in app errors
        debug && console.log(`update app state ${Object.keys(action.value ?? {})}`, action);
        return Object.assign({}, state, update);
      }
    case ACTIONS.RECEIVE_CHANGE_USER_ROLE_RESULT:
      {
        const result = action.value;
        if (result.data) {
          update.roleArbitrationInstance = result.data;
          update.userSelectedGroup = result.data.groupName;
          AppLocalStorage.set(NavSections.HOME, 'lastUserSelectedGroupName', update.userSelectedGroup);
        }
        //TODO General in app errors
        return Object.assign({}, state, update);
      }
    case ACTIONS.RECEIVE_LOGOUT:
      {
        const newInitialState = getInitialState();
        newInitialState.applicationData = new NodeEdgeMapper();
        newInitialState.largeObjects = {};
        newInitialState.userSelectedGroup = undefined;
        Object.assign(newInitialState, preLoads); // Other states may be unexpectedly removed. This will need to be looked at in the future
        debug && console.log('reloading initial state', newInitialState);
        return newInitialState;
      }
    /*
    case ACTIONS.API_TRANSACTION_START: {
      const { type, value: { transactionId, apiRef, path }} = action as API_TRANSACTION_START
      update.inFlightTransactions[ transactionId ] = { apiRef, path }
      return Object.assign( {}, state, update )
    }
         case ACTIONS.API_TRANSACTION_END: {
      const { type, value: { transactionId }} = action as API_TRANSACTION_END
      const clone = { ...update.inFlightTransactions }
      delete clone[transactionId]
      update.inFlightTransactions = clone
      debug && console.log('Open transaction count', Object.keys(update.inFlightTransactions).length )
     
      return Object.assign( {}, state, update )
    }
         */
    default:
      {
        //console.log('Reached Default with action:', action.type)
        return {
          ...state
        };
      }
  }
};
export default AppReducer;